import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import * as diff from 'diff';

/**
 * Coerce any prop value to a string before handing it to the `diff` library,
 * which calls `.match()` on its inputs and would otherwise throw
 * `value.match is not a function` on a non-string (T1.5).
 */
const toText = (value) => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  try {
    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  } catch {
    return String(value);
  }
};

/**
 * DiffViewer component - Shows side-by-side or inline differences between original and modified text
 *
 * @component
 */
const DiffViewer = ({ original, modified }) => {
  const renderDiff = () => {
    const originalStr = toText(original);
    const modifiedStr = toText(modified);
    if (!originalStr || !modifiedStr) return null;

    // Generate line-by-line diff
    const differences = diff.diffLines(originalStr, modifiedStr);

    // The whole `differences` array is recomputed from `originalStr`/`modifiedStr` on
    // every render (no add/remove/reorder UI, no per-chunk local state), so a key only
    // needs to stay unique within one render. Built from each chunk's own position in
    // the diffed text (running offset), not the loop index the rule flags, so two
    // identical chunks at different offsets still get distinct keys.
    let offset = 0;
    return differences.map((part) => {
      const chunkKey = `${offset}-${part.added ? 'a' : part.removed ? 'r' : 'c'}`;
      offset += part.value.length;

      // Styling based on addition/removal
      const style = {
        color: part.added ? 'green' : part.removed ? 'red' : 'inherit',
        backgroundColor: part.added ? '#e6ffed' : part.removed ? '#ffebe9' : 'inherit',
        padding: '2px 0',
        whiteSpace: 'pre-wrap'
      };

      // Add + or - prefix
      const prefix = part.added ? '+ ' : part.removed ? '- ' : '  ';

      return (
        <Typography key={chunkKey} component="div" style={style} variant="body2" fontFamily="monospace">
          {prefix}{part.value}
        </Typography>
      );
    });
  };

  return (
    <Box sx={{
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      p: 2,
      maxHeight: '500px',
      overflow: 'auto'
    }}>
      {renderDiff()}
    </Box>
  );
};

DiffViewer.propTypes = {
  original: PropTypes.string,
  modified: PropTypes.string
};

export default DiffViewer;